import React, { useEffect, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { type CareerProgress } from '../../models/CareerProgress';
import { formatDateOnlyLocal } from '../../utils/time';
import ProgressIndicators from '../shared/ProgressIndicators';
import ExpandableCardList from '../shared/ExpandableCardList';

interface CareerViewProps {
  driverId: string | undefined;
}

const CareerView: React.FC<CareerViewProps> = ({ driverId }) => {
  const [careerProgress, setCareerProgress] = useState<CareerProgress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchCareerProgress = async () => {
      if (!driverId) return;
      setIsLoading(true);
      setError('');
      try {
        const result = await driverApi.getDriverCareerProgress(driverId);
        if (result) {
          setCareerProgress(result);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load career progress');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCareerProgress();
  }, [driverId]);

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const renderProgressIndicator = (progress: CareerProgress) => {
     const circleClass = progress.numberOfStages > 10 ? 'd-none d-md-flex' : 'd-flex';
     return (
    <>
      <div className={`d-flex align-items-center gap-1 ${circleClass}`}>
        <ProgressIndicators completed={progress.currentStage} total={progress.numberOfStages} />
      </div>
      <span className="ms-1 text-white" style={{ fontSize: '1rem' }}>
        {progress.currentStage}/{progress.numberOfStages}
      </span>
    </>
  );
};

  const renderTableRow = (progress: CareerProgress) => (
    <>
      {progress.stageResults && progress.stageResults.map((stage) => (
        <tr key={stage.stageId}>
          <td className="text-white">{stage.stageId}</td>
          <td className="text-white hide-mobile">{stage.trackFriendlyName}</td>
          <td className="text-white">{stage.carFriendlyName}</td>
          <td className="text-white hide-mobile">{formatDateOnlyLocal(stage.date)}</td>
          <td className="text-white">{stage.level}</td>
        </tr>
      ))}
    </>
  );

  return (
    <ExpandableCardList
      data={careerProgress}
      isLoading={isLoading}
      error={error}
      emptyMessage="You don't have any career progress yet."
      expandedItems={expandedItems}
      onToggleExpanded={toggleExpanded}
      renderTitle={(progress) => progress.challengeName}
      renderProgressIndicator={renderProgressIndicator}
      tableHeaders={[
        { label: 'Stage' },
        { label: 'Track', hideMobile: true },
        { label: 'Car', },
        { label: 'Date', hideMobile: true },
        { label: 'Level' },
      ]}
      renderTableRow={renderTableRow}
      hasExpandableContent={(progress) => !!(progress.stageResults && progress.stageResults.length > 0)}
    />
  );
};

export default CareerView;

